import { Fragment } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { MarketOverviewStatisticsResultDto } from 'api/investor-client';
import { useFormatMoney } from 'hooks';
import { moveDecimalPoint, toPercent } from 'utils';

type MarketOverviewSummaryDto = MarketOverviewStatisticsResultDto['summary'];

interface Props {
  summary?: MarketOverviewSummaryDto;
  includeRecovery?: boolean;
}

const sections: [keyof MarketOverviewSummaryDto, string][][] = [
  [
    ['currentPortfolio', 'current_portfolio'],
    ['performing', 'performing'],
    ['delayed1To30', 'delayed_1_30'],
    ['delayed31To60', 'delayed_31_60'],
    ['delayed61To89', 'delayed_61_89'],
    ['delayed90Plus', 'delayed_90_plus'],
  ],
  [
    ['historicPortfolio', 'historic_portfolio'],
    ['repaid', 'repaid'],
    ['defaulted', 'defaulted'],
    ['recovery', 'recovery'],
  ],
];

export const FinancedPortfolioTable = ({ summary, includeRecovery = false }: Props) => {
  const formatMoney = useFormatMoney();

  return (
    <TableContainer>
      <Table size="small" sx={{ minWidth: 640, '& .MuiTableCell-root': { fontSize: 14, color: 'text.primary' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '28%' }} />
            {['principal', 'interest', 'total', 'share'].map((column) => (
              <TableCell key={column} align="right" sx={{ width: '18%', fontWeight: 700 }}>
                <FormattedMessage id={`market_overview.column.${column}`} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sections.map((rows, sectionIndex) => (
            <Fragment key={sectionIndex}>
              {rows.filter(([key]) => includeRecovery || key !== 'recovery').map(([key, label], index) => {
                const row = summary?.[key];
                const isTotal = index === 0;

                return (
                  <TableRow key={key} sx={{
                    '& .MuiTableCell-root': {
                      fontWeight: isTotal ? 700 : 400,
                      borderTopWidth: isTotal && sectionIndex > 0 ? 5 : undefined,
                      borderTopStyle: isTotal && sectionIndex > 0 ? 'solid' : undefined,
                      borderTopColor: 'divider',
                      borderBottomWidth: isTotal ? 2 : 1,
                    },
                  }}>
                    <TableCell component="th" scope="row" align={isTotal ? 'left' : 'right'}>
                      <FormattedMessage id={`market_overview.row.${label}`} />
                    </TableCell>
                    {(['principal', 'interest', 'total'] as const).map((field) => (
                      <TableCell key={field} align="right">
                        {formatMoney(moveDecimalPoint(row?.[field] ?? 0, -2))}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      {isTotal ? (
                        sectionIndex > 0 && <FormattedMessage id="market_overview.column.share" />
                      ) : (
                        <Typography component="span" sx={{ fontSize: 'inherit', fontStyle: key === 'recovery' ? 'italic' : undefined }}>
                          {toPercent(row?.share ?? 0)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
